import React, { useState } from 'react';
import { 
  Mail, MessageCircle, Search, Download, Upload, Plus, MoreVertical, UtensilsCrossed,
  Users, Clock, Send, Check, X, Eye, Grid, List, Filter, AlertCircle, Tag,
  UserCheck, UserX, UserPlus, Phone, MapPin, Calendar, Trash2, Edit3
} from 'lucide-react';
import { eventsService } from '../../services/eventsService';
import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { GuestMessagingModal } from './GuestMessagingModal';

export const GuestsTab = ({ guests, bookId, onAdd, onEdit, onDelete, onRefresh, loading }) => {
  const [view, setView] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedGuests, setSelectedGuests] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [importError, setImportError] = useState(null);

  const GUEST_CATEGORIES = [
    'Family',
    'Church',
    'Friends',
    'Colleagues',
    'Neighbors',
    'VIP',
    'Other'
  ];
  
  const GUEST_STATUS = {
    pending: { label: 'Pending', color: 'gray', icon: Clock },
    invited: { label: 'Invited', color: 'purple', icon: Send },
    confirmed: { label: 'Confirmed', color: 'green', icon: Check },
    declined: { label: 'Declined', color: 'red', icon: X },
    attended: { label: 'Attended', color: 'blue', icon: UserCheck }
  };

  const filteredGuests = guests.filter(guest => {
    const matchesSearch = 
      guest.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.phone?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || guest.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || guest.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const getStatusStats = () => ({
    total: guests.length,
    pending: guests.filter(g => g.status === 'pending').length,
    invited: guests.filter(g => g.status === 'invited').length,
    confirmed: guests.filter(g => g.status === 'confirmed').length,
    declined: guests.filter(g => g.status === 'declined').length,
    attended: guests.filter(g => g.status === 'attended').length,
    totalPlusOnes: guests.reduce((sum, g) => sum + (g.confirmedPlusOnes || 0), 0)
  });

  const stats = getStatusStats();

  const handleSendMessage = () => {
    if (selectedGuests.size === 0) {
      alert('Please select guests to message');
      return;
    }
    setShowMessagingModal(true);
  };

  const handleStatusChange = async (guestId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [guestId]: true }));
    try {
      await eventsService.updateGuestStatus(bookId, guestId, newStatus);
      const guest = guests.find(g => g.id === guestId);
      if (guest) {
        onEdit({ ...guest, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [guestId]: false }));
    }
  };

  const toggleGuestSelection = (guestId) => {
    const newSelected = new Set(selectedGuests);
    if (newSelected.has(guestId)) {
      newSelected.delete(guestId);
    } else {
      newSelected.add(guestId);
    }
    setSelectedGuests(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedGuests.size === filteredGuests.length && filteredGuests.length > 0) {
      setSelectedGuests(new Set());
    } else {
      setSelectedGuests(new Set(filteredGuests.map(g => g.id)));
    }
  };

  // CSV Export
  const handleExport = () => {
    if (guests.length === 0) return;
    
    const headers = ['Name', 'Email', 'Phone', 'Category', 'RSVP Status', 'Plus Ones'];
    const rows = guests.map(guest => [
      guest.name || '',
      guest.email || '',
      guest.phone || '',
      guest.category || 'Other',
      guest.status || 'pending',
      guest.plusOnes || '0'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `guests_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportError(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setImportError('CSV file is empty or invalid');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
      
      // Validate headers - only Name is required
      if (!headers.includes('name')) {
        setImportError('CSV must contain a "Name" column');
        return;
      }

      const nameIndex = headers.indexOf('name');
      const emailIndex = headers.indexOf('email');
      const phoneIndex = headers.indexOf('phone');
      const categoryIndex = headers.indexOf('category');
      const statusIndex = headers.indexOf('rsvp status') !== -1 
        ? headers.indexOf('rsvp status') 
        : headers.indexOf('status');
      const dietaryIndex = headers.indexOf('dietary restrictions') !== -1
        ? headers.indexOf('dietary restrictions')
        : headers.indexOf('dietary');

      let imported = 0;
      let failed = 0;

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        
        if (values.length < 1) continue;

        const name = values[nameIndex]?.trim();

        if (!name) {
          failed++;
          continue;
        }

        try {
          const guestData = {
            name,
            email: emailIndex !== -1 ? values[emailIndex]?.trim() || '' : '',
            phone: phoneIndex !== -1 ? values[phoneIndex]?.trim() || '' : '',
            category: categoryIndex !== -1 && GUEST_CATEGORIES.includes(values[categoryIndex]?.trim()) 
              ? values[categoryIndex].trim() 
              : 'Other',
            status: statusIndex !== -1 && ['pending', 'invited', 'confirmed', 'declined', 'attended'].includes(values[statusIndex]?.trim().toLowerCase())
              ? values[statusIndex].trim().toLowerCase()
              : 'pending',
            dietaryRestrictions: dietaryIndex !== -1 ? values[dietaryIndex]?.trim() || '' : '',
            plusOnes: 0,
            confirmedPlusOnes: 0,
            notes: ''
          };

          // Use eventsService directly to add guest
          await eventsService.addGuest(bookId, guestData);
          imported++;
        } catch (error) {
          console.error('Error importing guest:', error);
          failed++;
        }
      }

      if (imported > 0) {
        alert(`Successfully imported ${imported} guest(s)${failed > 0 ? `, ${failed} failed` : ''}`);
        if (onRefresh) onRefresh(); // Refresh the guest list
      }
        else {
        setImportError('No valid guests found in CSV');
      }
    } catch (error) {
      console.error('Error processing CSV:', error);
      setImportError('Failed to process CSV file');
    }

    // Reset file input
    e.target.value = '';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header - same as before */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="h-7 w-7 text-emerald-600" />
            Guest List
          </h3>
          <p className="text-gray-600 mt-1">
            Manage guests and send invitations
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowStats(!showStats)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              showStats ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Eye className="h-4 w-4" />
            Stats
          </button>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              showFilters ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('cards')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                view === 'cards' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                view === 'list' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          <button
            onClick={onAdd}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Guest
          </button>
        </div>
      </div>

      {/* Stats Cards - same as before */}
      {showStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <StatCard label="Total" value={stats.total} icon={Users} color="blue" />
          <StatCard label="Pending" value={stats.pending} icon={Clock} color="gray" />
          <StatCard label="Invited" value={stats.invited} icon={Send} color="purple" />
          <StatCard label="Confirmed" value={stats.confirmed} icon={Check} color="green" />
          <StatCard label="Declined" value={stats.declined} icon={X} color="red" />
          <StatCard label="Attended" value={stats.attended} icon={UserCheck} color="blue" />
          <StatCard label="Plus Ones" value={stats.totalPlusOnes} icon={UserPlus} color="orange" />
        </div>
      )}

      {/* Search and Filters - same as before */}
      <div className="flex flex-col md:flex-row gap-3">
        
          <Search className="absolute mt-4 ml-2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search guests by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />

          {/* Filters */}

          {showFilters && (
              <>
               
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-8 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">All Categories</option>
                  {GUEST_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-6 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="invited">Invited</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="declined">Declined</option>
                  <option value="attended">Attended</option>
                </select>
                
                <label className="flex items-center gap-2 px-4 py-2 bg-blue-300 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">Import CSV</span>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImport}
                    className="hidden"
                  />
                </label>

                <button
                  onClick={handleExport}
                  disabled={guests.length === 0}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">Export CSV</span>
                </button>
              
              </>
            )}
        
      </div>

      {/* Import Error */}
      {importError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{importError}</p>
          </div>
          <button
            onClick={() => setImportError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Bulk Actions - UPDATED */}
      {selectedGuests.size > 0 && (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center text-white font-bold">
                {selectedGuests.size}
              </div>
              <div>
                <p className="font-medium text-emerald-900">
                  {selectedGuests.size} guest{selectedGuests.size > 1 ? 's' : ''} selected
                </p>
                <p className="text-sm text-emerald-700">
                  Send messages with customizable templates and media
                </p>
              </div>
            </div>
            
            <button
              onClick={handleSendMessage}
              disabled={sending}
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition disabled:opacity-50 flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
              Send Message
            </button>
          </div>
        </div>
      )}

      {/* Guests Display */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      ) : filteredGuests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No guests found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters' 
              : 'Start building your guest list'}
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <button
              onClick={onAdd}
              className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Your First Guest
            </button>
          )}
        </div>
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGuests.map(guest => (
            <GuestCard
              key={guest.id}
              guest={guest}
              selected={selectedGuests.has(guest.id)}
              onSelect={() => toggleGuestSelection(guest.id)}
              onEdit={() => onEdit(guest)}
              onDelete={() => onDelete(guest.id)}
              onStatusChange={(status) => handleStatusChange(guest.id, status)}
              updating={updatingStatus[guest.id]}
              GUEST_STATUS={GUEST_STATUS}
            />
          ))}
        </div>
      ) : (
        <GuestListView
          guests={filteredGuests}
          selectedGuests={selectedGuests}
          toggleSelectAll={toggleSelectAll}
          toggleGuestSelection={toggleGuestSelection}
          onEdit={onEdit}
          onDelete={onDelete}
          onStatusChange={handleStatusChange}
          updatingStatus={updatingStatus}
          GUEST_STATUS={GUEST_STATUS}
        />
      )}

      {/* Messaging Modal */}
      {showMessagingModal && (
        <GuestMessagingModal
          bookId={bookId}
          selectedGuests={guests.filter(g => selectedGuests.has(g.id))}
          onClose={() => {
            setShowMessagingModal(false);
            setSelectedGuests(new Set());
          }}
        />
      )}
    </div>
  );
};

// Stat Card Component - same as before
const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    gray: 'bg-gray-100 text-gray-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Guest Card Component
const GuestCard = ({ 
  guest, 
  selected, 
  onSelect, 
  onEdit, 
  onDelete, 
  onStatusChange, 
  updating,
  GUEST_STATUS 
}) => {
  const [showActions, setShowActions] = useState(false);
  const statusConfig = GUEST_STATUS[guest.status];
  const StatusIcon = statusConfig.icon;

  return (
    <div className={`bg-white rounded-xl border-2 p-4 transition-all ${
      selected ? 'border-emerald-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            className="mt-1 w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 truncate">{guest.name}</h4>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-1 bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
          </div>
        </div>

        {/* Simple Action Buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded transition"
            title="Edit guest"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-1.5 hover:bg-gray-100 rounded transition"
              title="More actions"
            >
              <MoreVertical className="w-4 h-4 text-gray-500" />
            </button>
            
            {showActions && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-10">
                <button
                  onClick={() => { onStatusChange('confirmed'); setShowActions(false); }}
                  disabled={updating}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-green-600"
                >
                  <Check className="w-4 h-4" />
                  Mark Confirmed
                </button>
                <button
                  onClick={() => { onStatusChange('declined'); setShowActions(false); }}
                  disabled={updating}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                >
                  <X className="w-4 h-4" />
                  Mark Declined
                </button>
                <button
                  onClick={() => { onStatusChange('attended'); setShowActions(false); }}
                  disabled={updating}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-blue-600"
                >
                  <UserCheck className="w-4 h-4" />
                  Mark Attended
                </button>
                <hr className="my-1" />
                <button
                  onClick={() => {
                    if (window.confirm('Delete this guest?')) {
                      onDelete();
                    }
                    setShowActions(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Guest
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="space-y-2 text-sm">
        {guest.email && (
          <div className="flex items-center gap-2 text-gray-600">
            <Mail className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{guest.email}</span>
          </div>
        )}
        {guest.phone && (
          <div className="flex items-center gap-2 text-gray-600">
            <Phone className="w-4 h-4 flex-shrink-0" />
            <span>{guest.phone}</span>
          </div>
        )}
        {guest.category && (
          <div className="flex items-center gap-2 text-gray-600">
            <Tag className="w-4 h-4 flex-shrink-0" />
            <span>{guest.category}</span>
          </div>
        )}
        {guest.plusOnes > 0 && (
          <div className="flex items-center gap-2 text-gray-600">
            <UserPlus className="w-4 h-4 flex-shrink-0" />
            <span>Plus {guest.confirmedPlusOnes || 0}/{guest.plusOnes}</span>
          </div>
        )}
      </div>

      {/* Invitation Info */}
      {guest.invitationSentAt && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            <span>
              Invited {new Date(guest.invitationSentAt).toLocaleDateString()}
              {guest.invitationMethod && ` via ${guest.invitationMethod}`}
            </span>
          </div>
        </div>
      )}

      {guest.notes && (
        <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
          {guest.notes}
        </div>
      )}
    </div>
  );
};

// Guest List View Component - UPDATED with Category and Dietary Restrictions
const GuestListView = ({ 
  guests, 
  selectedGuests, 
  toggleSelectAll, 
  toggleGuestSelection,
  onEdit,
  onDelete,
  onStatusChange,
  updatingStatus,
  GUEST_STATUS 
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <input
                  type="checkbox"
                  checked={selectedGuests.size === guests.length && guests.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden xl:table-cell">Dietary</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Plus Ones</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Invited</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {guests.map(guest => {
              const statusConfig = GUEST_STATUS[guest.status];
              const StatusIcon = statusConfig.icon;
              
              return (
                <tr key={guest.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedGuests.has(guest.id)}
                      onChange={() => toggleGuestSelection(guest.id)}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{guest.name}</div>
                    {guest.notes && (
                      <div className="text-xs text-gray-500 truncate max-w-xs">{guest.notes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                    <div className="space-y-1">
                      {guest.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span className="truncate max-w-[200px]">{guest.email}</span>
                        </div>
                      )}
                      {guest.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span>{guest.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                    {guest.category && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                        <Tag className="w-3 h-3" />
                        {guest.category}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden xl:table-cell">
                    {guest.dietaryRestrictions ? (
                      <span className="inline-flex items-center gap-1 text-xs">
                        <UtensilsCrossed className="w-3 h-3 text-gray-400" />
                        <span className="truncate max-w-[150px]" title={guest.dietaryRestrictions}>
                          {guest.dietaryRestrictions}
                        </span>
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                    {guest.plusOnes ? (
                      <span className="flex items-center gap-1">
                        <UserPlus className="w-3 h-3 text-gray-400" />
                        {guest.confirmedPlusOnes || 0}/{guest.plusOnes}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">
                    {guest.invitationSentAt 
                      ? new Date(guest.invitationSentAt).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onEdit(guest)}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded transition"
                        title="Edit guest"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete ${guest.name}?`)) {
                            onDelete(guest.id);
                          }
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                        title="Delete guest"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};